import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { toast } from "sonner";
import BottomTabNav from "../../components/BottomTabNav";
import { Phone, Zap } from "lucide-react";
import CallScreen from "../../components/CallScreen";

const LiveHelpPage = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCall, setActiveCall] = useState(null);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await api.get('/agents/available');
      setAgents(response.data);
    } catch (error) {
      toast.error("Failed to load agents");
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (agentId) => {
    try {
      const response = await api.post('/calls/request', {
        agent_id: agentId
      });
      setActiveCall({
        callRequestId: response.data.call_request_id,
        status: response.data.status,
        mockMode: response.data.mock_mode
      });
      toast.success("Connecting to agent...");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect");
    }
  };

  const handleCallEnd = () => {
    setActiveCall(null);
    fetchAgents();
  };

  if (activeCall) {
    return <CallScreen callData={activeCall} onEnd={handleCallEnd} />;
  }

  return (
    <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-ocean font-outfit" data-testid="live-help-page-title">Live Help</h1>
            <p className="text-slate-600 mt-1">Online Now ({agents.length})</p>
          </div>
        </div>

        {agents.length > 0 && (
          <button
            onClick={() => handleConnect(agents[0].id)}
            className="w-full bg-aviation hover:bg-sky-600 text-white rounded-2xl p-6 font-semibold text-lg shadow-lg shadow-aviation/20 transition-transform active:scale-95 mb-6 flex items-center justify-center gap-3"
            data-testid="connect-me-now-button"
          >
            <Zap className="w-6 h-6" />
            Connect me now
          </button>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading agents...</p>
          </div>
        ) : agents.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center" data-testid="no-agents-message">
            <Phone className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-ocean mb-2">No agents online right now</h3>
            <p className="text-slate-600 mb-6">Please try again later</p>
            <button
              onClick={fetchAgents}
              className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-full px-6 py-3 font-medium transition-colors"
              data-testid="try-again-button"
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all p-6"
                data-testid={`agent-card-${agent.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-aviation to-sky-600 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                    {agent.display_name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-ocean mb-1">{agent.display_name}</h3>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {agent.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-sage/30 text-ocean text-xs font-medium rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={() => handleConnect(agent.id)}
                      className="w-full bg-aviation hover:bg-sky-600 text-white rounded-full h-12 font-semibold shadow-lg shadow-aviation/20 transition-transform active:scale-95 flex items-center justify-center gap-2"
                      data-testid={`connect-button-${agent.id}`}
                    >
                      <Phone className="w-5 h-5" />
                      Connect
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomTabNav />
    </div>
  );
};

export default LiveHelpPage;
